import {Box, Button, Divider, FormControlLabel, Stack, Typography, useTheme} from '@mui/material';
import {
	AbandonFlowModal,
	AlertInfo,
	CTAButtonFixed,
	MainFooter,
	MainHeader,
	PageWrapperLayout,
	StyledGrid,
} from 'presentation/components';
import {DateFormatEnum, FeaturesEnum, RefundEnum} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {stringfyDate} from 'presentation/utils';
import React, {Fragment, useCallback, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {useExitFlowModalStore} from '../../pages/Request/store/useExitFlowModalStore';
import {useExitFlowSidebarRouteStore} from '../../pages/Request/store/useExitFlowSidebarRouteStore';
import type {RefundFlowNavigationState} from '../../types/refundFlowNavigationState';
import {useGetRefundTableUrl} from '../../hooks/useGetRefundTableUrl';
import {AdviceModal} from '../AdviceModal';
import {ServiceCard} from '../ServiceCard';
import {WhatCanRefundModal} from '../WhatCanRefundModal';
import {CommunicateModal} from './components/CommunicateModal';
import {ModalsType, ModalsTypeEnum} from './constants/modalType';
import {
	LinkText,
	StyledBox,
	StyledCheckbox,
	StyledContainer,
	StyledFooterContainer,
} from './styles';
import {RefundEnumTermsPathsType} from './types';
import {getTermsByType} from './constants/terms';

const {discardModal, title} = translations['pt-br'].privateFlow.refund;

const {caption, readAndAgree, viewRefundTable, whatCanBeRefunded, moreInfo, documentListAlert} =
	translations['pt-br'].privateFlow.refund.terms;
const {keep, cancelContract, refundCanceledContract, cancelContractIn, requiredField} =
	translations['pt-br'].commons;

export const TermsPage = () => {
	const navigate = useNavigate();
	const {pathname, state} = useLocation();
	const {refundTermsReturnTo} = (state ?? {}) as RefundFlowNavigationState;
	const exitFlowModal = useExitFlowModalStore(s => s.open);
	const resetExitFlowModal = useExitFlowModalStore(s => s.reset);
	const setExitFlowModal = useExitFlowModalStore(s => s.setOpen);
	const exitFlowSidebarRouteState = useExitFlowSidebarRouteStore(s => s.route);
	const resetExitFlowSidebarRouteState = useExitFlowSidebarRouteStore(s => s.reset);
	const {colors} = useTheme();
	const {isTablet, isMobile} = useMediaQueries();
	const isDefault = pathname === RefundEnum.TERMS;
	const {getRefundTableUrl, URL_IS_LOADING} = useGetRefundTableUrl();
	const [termsChecked, setTermsChecked] = useState(false);
	const [errorToSendState, setErrorToSendState] = useState<boolean>(false);
	const [isModalsOpen, setIsModalsOpen] = useState<Record<ModalsType, boolean>>({
		communicate: false,
		documents: false,
		whatCanBeRefund: false,
	});

	const handleChangeModals = (type: ModalsType) => {
		setIsModalsOpen(prev => ({
			...prev,
			[type]: !prev[type],
		}));
	};

	const {isEnabled: isNewPreviewMessageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_REQUEST_PREVIEW_MESSAGE,
	);
	const terms = getTermsByType(isNewPreviewMessageEnabled)[pathname as RefundEnumTermsPathsType];

	const handleBack = () => {
		navigate(refundTermsReturnTo ?? RefundEnum.REFUND);
	};
	const handleGoToForm = () => {
		navigate(terms.navigateTo, {
			state: refundTermsReturnTo ? {refundTermsReturnTo} : undefined,
		});
	};

	const handleTermsSidebarAbandonConfirm = useCallback(() => {
		resetExitFlowModal();
		if (exitFlowSidebarRouteState) {
			navigate(exitFlowSidebarRouteState);
			resetExitFlowSidebarRouteState();
			return;
		}
		navigate(refundTermsReturnTo ?? RefundEnum.REFUND);
	}, [
		exitFlowSidebarRouteState,
		navigate,
		refundTermsReturnTo,
		resetExitFlowModal,
		resetExitFlowSidebarRouteState,
	]);
	const handleKeep = () => {
		if (termsChecked === false) {
			setErrorToSendState(true);
		} else {
			handleGoToForm();
		}
	};

	const isAdditionalDocuments = pathname === RefundEnum.TERMS_ADDITIONAL_DOCS;

	const {contractCancellationDate, isValid, isPlanCancelledDatesAreEqual} = useContractStatus();

	const contractCancellationMessage = (() => {
		if (!isPlanCancelledDatesAreEqual) {
			return cancelContract;
		}
		const formattedDate = contractCancellationDate
			? stringfyDate(contractCancellationDate, DateFormatEnum.DATE_FORMAT_8)
			: '';
		return `${cancelContractIn}${formattedDate}`;
	})();

	const isErrorColor = errorToSendState ? colors.light.error.main : 'text.primary';

	return (
		<PageWrapperLayout title={title}>
			<MainHeader
				title={terms.title}
				handleBack={handleBack}
				variant="subRoute"
				handleClose={handleBack}
			/>
			{!isValid ? (
				<AlertInfo.Root
					severity="warning"
					sx={isMobile ? {margin: '16px 16px 0'} : {padding: '8px 16px 6px'}}>
					<AlertInfo.Title title={contractCancellationMessage} />
					<AlertInfo.Description>
						<Typography variant="body2">{refundCanceledContract}</Typography>
					</AlertInfo.Description>
				</AlertInfo.Root>
			) : null}
			<StyledContainer>
				<StyledGrid.Root>
					<StyledGrid.Item md={12} lg={isDefault ? 8 : 12}>
						<StyledBox>
							<Typography variant="h6" color="text.primary" textAlign="center">
								{terms.subtitle}
							</Typography>

							{isDefault ? (
								<AlertInfo.Root severity="warning">
									<AlertInfo.Description>
										<Typography variant="body2">
											<b>{documentListAlert.part1}</b>
											{documentListAlert.part2}
										</Typography>
									</AlertInfo.Description>
								</AlertInfo.Root>
							) : null}

							<Stack>
								{terms.items.map(item => (
									<Stack
										direction="row"
										alignItems="center"
										key={item.iconName}
										gap={4}
										py={'12px'}>
										<Icon
											style={{minWidth: 24}}
											name={item.iconName}
											color={colors.light.text.secondary}
										/>
										<Stack>
											{typeof item.title === 'string' ? (
												<Typography variant="body1" color="text.primary">
													{item.title}
													{item.strongText ? (
														<strong> {item.strongText}</strong>
													) : null}
												</Typography>
											) : (
												item.title
											)}
											{item.action ? (
												<Box>
													<Button
														data-tag-id={item.dataTagId}
														variant="text"
														size="small"
														onClick={() =>
															handleChangeModals(
																item.action?.modal || 'communicate',
															)
														}>
														<LinkText>{item.action?.name}</LinkText>
													</Button>
												</Box>
											) : null}
										</Stack>
									</Stack>
								))}
							</Stack>

							{terms.showCaption ? (
								<Fragment>
									<Divider />

									<Typography variant="caption" color="text.secondary">
										{caption}
									</Typography>
								</Fragment>
							) : null}

							{!isMobile ? <Divider /> : null}

							<Box px={2}>
								<CTAButtonFixed.Root
									direction={isTablet ? 'column' : 'row'}
									justifyContent="space-between"
									paddingX={4}>
									<FormControlLabel
										sx={{alignItems: errorToSendState ? 'end' : 'center'}}
										onChange={(_, checked) => {
											setTermsChecked(checked);
											setErrorToSendState(false);
										}}
										control={
											<StyledCheckbox
												data-tag-id={
													isAdditionalDocuments
														? RefundTaggingEnum.CLICK_LI_E_CONCORDO_DADOS_COMPLEMENTARES
														: undefined
												}
												checked={termsChecked}
												errorToSendState={errorToSendState}
											/>
										}
										label={
											<Fragment>
												<Typography variant="body1" color={isErrorColor}>
													{readAndAgree}
												</Typography>
												{errorToSendState ? (
													<Typography
														variant="helperText"
														color={isErrorColor}>
														{requiredField}
													</Typography>
												) : null}
											</Fragment>
										}
									/>
									<CTAButtonFixed.Action
										data-tag-id={terms.dataTagId}
										size="large"
										onClick={handleKeep}>
										{keep}
									</CTAButtonFixed.Action>
								</CTAButtonFixed.Root>
							</Box>
						</StyledBox>
					</StyledGrid.Item>

					{isDefault ? (
						<StyledGrid.Item md={12} lg={4}>
							<Stack spacing={1}>
								<Typography variant="subtitle2" color="text.secondary">
									{moreInfo}
								</Typography>

								<ServiceCard
									icon="info-filled"
									title={whatCanBeRefunded}
									onClick={() =>
										handleChangeModals(ModalsTypeEnum.WHAT_CAN_BE_REFUND)
									}
									dataTagId={RefundTaggingEnum.CLICK_O_QUE_PODE_SER_REEMBOLSADO}
								/>
								<ServiceCard
									isLoading={URL_IS_LOADING}
									isExternalLink
									onClick={getRefundTableUrl}
									title={viewRefundTable}
									icon="table-view-filled"
									dataTagId={RefundTaggingEnum.CLICK_VISUALIZAR_TABELA_REEMBOLSO}
								/>
							</Stack>
						</StyledGrid.Item>
					) : null}
				</StyledGrid.Root>
			</StyledContainer>

			<CommunicateModal
				open={isModalsOpen.communicate}
				setOpen={() => handleChangeModals(ModalsTypeEnum.COMMUNICATE)}
			/>
			<AdviceModal
				open={isModalsOpen.documents}
				setOpen={() => handleChangeModals(ModalsTypeEnum.DOCUMENTS)}
			/>
			<WhatCanRefundModal
				open={isModalsOpen.whatCanBeRefund}
				setOpen={() => handleChangeModals(ModalsTypeEnum.WHAT_CAN_BE_REFUND)}
			/>
			{pathname === RefundEnum.TERMS ? (
				<AbandonFlowModal
					closeButtondataTagId={RefundTaggingEnum.CLICK_FECHAR_ICONE_MODAL_ABANDONO_REEMBOLSO}
					backButtondataTagId={RefundTaggingEnum.CLICK_VOLTAR_MODAL_ABANDONO_REEMBOLSO}
					modaldataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_ABANDONO_REEMBOLSO}
					confirmButtondataTagId={
						RefundTaggingEnum.CLICK_CONFIRMAR_BOTAO_MODAL_ABANDONO_REEMBOLSO
					}
					open={exitFlowModal}
					setOpen={setExitFlowModal}
					handleConfirm={handleTermsSidebarAbandonConfirm}
					title={discardModal.title}
					headerTitle={discardModal.headerTitle}
					description={discardModal.description}
				/>
			) : null}
			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
		</PageWrapperLayout>
	);
};
